import React, { useState, useEffect, useMemo, useRef } from 'react';
import Menu from './Menu';
import apiCall from '../../utils/api';
import {
  ContentContainer,
  TableContainer,
  StyledTable,
  Th,
  Td,
  Tr,
  TableTitle,
  RowNumberTh,
  RowNumberTd,
  RefreshButton,
  GraphTitle,
  TitleWithAction,
} from './AnalyticsStyles';
import {
  FilterContainer,
  FilterButton,
  ExistingFiltersList,
  FilterItem,
  FilterName,
  DeleteButton,
  FormGrid,
  FullWidthInput,
  FormGroup,
  FormLabel,
  FilterInput,
  DomainSelectorContainer,
  DropdownList,
  DropdownItem,
  SelectedItemsContainer,
  ItemTag,
  RemoveTagButton,
  FilterInfo,
  FilterCriteriaContainer,
  CriteriaLabel,
  LeadsTh,
  LeadsTd,
} from './LeadsStyles';
import { domainIconMapRaw, domainIconsFormacodesSousGrandDomaines } from '../Onboarding/domainIconMap';
import { regions, departements } from './locations'; // Import location data

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isCreatingFilter, setIsCreatingFilter] = useState(false);

  // --- Form State ---
  const [newFilterName, setNewFilterName] = useState('');
  
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [domainSearch, setDomainSearch] = useState(null);
  const domainInputRef = useRef(null);

  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState(null);
  const locationInputRef = useRef(null);
  
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const [userTypeSearch, setUserTypeSearch] = useState(null);
  const userTypeInputRef = useRef(null);

  const [selectedBacTypes, setSelectedBacTypes] = useState([]);
  const [bacTypeSearch, setBacTypeSearch] = useState(null);
  const bacTypeInputRef = useRef(null);

  const [selectedSpecialites, setSelectedSpecialites] = useState([]);
  const [specialiteSearch, setSpecialiteSearch] = useState(null);
  const specialiteInputRef = useRef(null);

  const allDomains = useMemo(() => {
    const combined = { ...domainIconMapRaw, ...domainIconsFormacodesSousGrandDomaines };
    return [...new Set(Object.keys(combined))].sort();
  }, []);

  const allUserTypes = useMemo(() => ['seconde', 'premiere', 'terminale', 'bac1', 'student'], []);
  const allLocations = useMemo(() => [...new Set([...regions, ...departements])].sort(), []);
  const allBacTypes = useMemo(() => ['Général', 'Technologique', 'Professionnel'], []);

  const fetchFilters = async () => {
    try {
      setLoadingFilters(true);
      const response = await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/lead-filters/`,
        'GET'
      );
      setFilters(response);
    } catch (err) {
      setError('Failed to fetch filters.');
      console.error("Error fetching filters:", err);
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchLeads = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      // Step 1: Trigger the backend to scan all users and generate new leads.
      console.log("Triggering backend lead generation process. Check server logs for details.");
      await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/refresh-leads/`,
        'POST'
      );
      
      // Step 2: Fetch the updated list of leads, including any new ones.
      const response = await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/leads/?_=${new Date().getTime()}`,
        'GET'
      );
      setLeads(response);
    } catch (err) {
      setError('Failed to refresh and fetch leads.');
      console.error("Error refreshing/fetching leads:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // This initial fetch just gets the current leads without triggering a full refresh.
    const initialFetchLeads = async () => {
        setLoadingLeads(true);
        try {
            const response = await apiCall(
                `${process.env.REACT_APP_BACKEND_URL}/manager/leads/?_=${new Date().getTime()}`,
                'GET'
            );
            setLeads(response);
        } catch (err) {
            setError('Failed to fetch leads.');
            console.error("Error fetching leads:", err);
        } finally {
            setLoadingLeads(false);
        }
    };

    initialFetchLeads();
    fetchFilters();
  }, []);

  const handleCreateFilter = async (e) => {
    e.preventDefault();
    setError(null);
    setIsCreatingFilter(true);
    try {
      const payload = {
        name: newFilterName,
        domains: selectedDomains,
        locations: selectedLocations,
        user_types: selectedUserTypes,
        type_bac: selectedBacTypes,
        specialites: selectedSpecialites,
      };

      // The backend returns the newly created filter, which we can use to update state
      const newFilter = await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/lead-filters/`,
        'POST',
        payload
      );
      
      // Add the new filter to the state directly to update the UI
      setFilters(prevFilters => [...prevFilters, newFilter]);
      
      // Reset form fields
      setNewFilterName('');
      setSelectedDomains([]);
      setSelectedLocations([]);
      setSelectedUserTypes([]);
      setSelectedBacTypes([]);
      setSelectedSpecialites([]);

    } catch (err) {
      setError('Impossible de créer le filtre.');
    } finally {
      setIsCreatingFilter(false);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    if (!filterId) {
      console.error("Delete cancelled: filterId is invalid.");
      return;
    }
    // Optimistic UI update: remove from state first
    setFilters(prev => prev.filter(f => f.id !== filterId));
    try {
      await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/lead-filters/${filterId}/`,
        'DELETE'
      );
    } catch (err) {
      setError('Failed to delete filter. It may have already been removed.');
      console.error("Error deleting filter:", err);
      // Rollback on error
      fetchFilters(); 
    }
  };

  const createMultiSelectHandlers = (state, setState, search, setSearch, inputRef) => ({
    containerRef: inputRef,
    isOpen: search !== null,
    setIsOpen: (isOpen) => setSearch(isOpen ? '' : null),
    selectedItems: state,
    search: search ?? '',
    setSearch,
    onAddItem: (item) => {
      if (!state.includes(item)) setState([...state, item]);
      setSearch(null);
    },
    onRemoveItem: (item) => setState(state.filter(i => i !== item)),
  });

  const bacTypeHandlers = createMultiSelectHandlers(selectedBacTypes, setSelectedBacTypes, bacTypeSearch, setBacTypeSearch, bacTypeInputRef);
  const specialiteHandlers = createMultiSelectHandlers(selectedSpecialites, setSelectedSpecialites, specialiteSearch, setSpecialiteSearch, specialiteInputRef);
  const domainHandlers = createMultiSelectHandlers(selectedDomains, setSelectedDomains, domainSearch, setDomainSearch, domainInputRef);
  const locationHandlers = createMultiSelectHandlers(selectedLocations, setSelectedLocations, locationSearch, setLocationSearch, locationInputRef);
  const userTypeHandlers = createMultiSelectHandlers(selectedUserTypes, setSelectedUserTypes, userTypeSearch, setUserTypeSearch, userTypeInputRef);

  const renderMultiSelect = (label, placeholder, allItems, handlers) => (
    <FormGroup ref={handlers.containerRef}>
      <FormLabel>{label}</FormLabel>
      <DomainSelectorContainer onBlur={() => setTimeout(() => handlers.setIsOpen(false), 200)}>
        <FilterInput 
          type="text" 
          placeholder={placeholder}
          value={handlers.search}
          onChange={e => handlers.setSearch(e.target.value)}
          onFocus={() => handlers.setIsOpen(true)}
        />
        {handlers.isOpen && (
          <DropdownList>
            {allItems.filter(item => !handlers.selectedItems.includes(item) && item.toLowerCase().includes(handlers.search.toLowerCase())).map(item => (
              <DropdownItem key={item} onMouseDown={() => handlers.onAddItem(item)}>{item}</DropdownItem>
            ))}
          </DropdownList>
        )}
      </DomainSelectorContainer>
      <SelectedItemsContainer>
        {handlers.selectedItems.map(item => (
          <ItemTag key={item}>
            {item}
            <RemoveTagButton onClick={() => handlers.onRemoveItem(item)}>&times;</RemoveTagButton>
          </ItemTag>
        ))}
      </SelectedItemsContainer>
    </FormGroup>
  );

  return (
    <Menu>
      <ContentContainer>
        <FilterContainer>
          <GraphTitle>Mes filtres de leads</GraphTitle>
          
          {loadingFilters ? <p>Chargement des filtres...</p> : (
            <ExistingFiltersList>
              {filters.map(filter => (
                <FilterItem key={filter.id}>
                    <FilterInfo>
                        <FilterName>{filter.name}</FilterName>
                        <FilterCriteriaContainer>
                            {filter.domains && filter.domains.length > 0 && (
                                <ItemTag>
                                    <CriteriaLabel>{filter.domains.length > 1 ? 'Domaines' : 'Domaine'}:</CriteriaLabel>
                                    {filter.domains.join(', ')}
                                </ItemTag>
                            )}
                            {filter.locations && filter.locations.length > 0 && (
                                <ItemTag>
                                    <CriteriaLabel>{filter.locations.length > 1 ? 'Lieux' : 'Lieu'}:</CriteriaLabel>
                                    {filter.locations.join(', ')}
                                </ItemTag>
                            )}
                            {filter.user_types && filter.user_types.length > 0 && (
                                <ItemTag>
                                    <CriteriaLabel>{filter.user_types.length > 1 ? 'Niveaux' : 'Niveau'}:</CriteriaLabel>
                                    {filter.user_types.map(userType => userType.charAt(0).toUpperCase() + userType.slice(1)).join(', ')}
                                </ItemTag>
                            )}
                            {filter.type_bac && filter.type_bac.length > 0 && <ItemTag><CriteriaLabel>Bac:</CriteriaLabel>{filter.type_bac.join(', ')}</ItemTag>}
                            {filter.specialites && filter.specialites.length > 0 && <ItemTag><CriteriaLabel>Spés:</CriteriaLabel>{filter.specialites.join(', ')}</ItemTag>}
                        </FilterCriteriaContainer>
                    </FilterInfo>
                    <DeleteButton onClick={() => handleDeleteFilter(filter.id)}>
                        &times;
                    </DeleteButton>
                </FilterItem>
              ))}
            </ExistingFiltersList>
          )}

          <form onSubmit={handleCreateFilter} style={{ marginTop: '2rem' }}>
            <FormGrid>
              <FormGroup>
                <FormLabel htmlFor="name">Nom du filtre</FormLabel>
                <FilterInput id="name" name="name" type="text" placeholder="Ex: Étudiants en Droit à Paris" value={newFilterName} onChange={e => setNewFilterName(e.target.value)} required />
              </FormGroup>

              {renderMultiSelect("Domaines", "Rechercher un domaine...", allDomains, domainHandlers)}
              {renderMultiSelect("Région / Département", "Rechercher un lieu...", allLocations, locationHandlers)}
              {renderMultiSelect("Niveau d'étude", "Sélectionner un ou plusieurs niveaux", allUserTypes, userTypeHandlers)}
              {renderMultiSelect("Type de Bac", "Rechercher un type de bac...", allBacTypes, bacTypeHandlers)}
              {renderMultiSelect("Spécialités", "Rechercher une spécialité...", [], specialiteHandlers)}
            </FormGrid>
            <div style={{textAlign: 'right', marginTop: '1rem'}}>
                <FilterButton type="submit" disabled={isCreatingFilter}>
                  {isCreatingFilter ? 'Création en cours...' : 'Créer le filtre'}
                </FilterButton>
            </div>
          </form>
          {error && <p style={{ color: 'red', marginTop: '1rem' }}>{error}</p>}
        </FilterContainer>

        <TitleWithAction>
          <TableTitle>Mes leads capturés</TableTitle>
          <RefreshButton onClick={fetchLeads} disabled={isRefreshing}>
            {isRefreshing ? '...' : 'Rafraîchir'}
          </RefreshButton>
        </TitleWithAction>

        <TableContainer>
          {loadingLeads ? (
            <p>Chargement des leads...</p>
          ) : leads.length > 0 ? (
            <StyledTable>
              <thead>
                <Tr>
                  <RowNumberTh>#</RowNumberTh>
                  <LeadsTh>Dernière connexion</LeadsTh>
                  <LeadsTh>Type</LeadsTh>
                  <LeadsTh>Pseudo</LeadsTh>
                  <LeadsTh>Email</LeadsTh>
                  <LeadsTh>Téléphone</LeadsTh>
                  <LeadsTh>Niveau</LeadsTh>
                  <LeadsTh>Lycée</LeadsTh>
                  <LeadsTh>Ville</LeadsTh>
                  <LeadsTh>Domaines</LeadsTh>
                  <LeadsTh>Type Bac</LeadsTh>
                  <LeadsTh>Spécialités</LeadsTh>
                  <LeadsTh>Notes</LeadsTh>
                  <LeadsTh>Infos</LeadsTh>
                </Tr>
              </thead>
              <tbody>
                {leads.map((lead, index) => (
                  <Tr key={lead.id}>
                    <RowNumberTd>{index + 1}</RowNumberTd>
                    <LeadsTd>{lead.user.last_login || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.source_filter?.name || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.pseudo || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.email || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.phone_number || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.user_type || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.lycee_name || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.user_city || 'N/A'}</LeadsTd>
                    <LeadsTd>{(lead.user.domaines || []).join(', ') || 'N/A'}</LeadsTd>
                    <LeadsTd>{lead.user.type_bac || 'N/A'}</LeadsTd>
                    <LeadsTd>{(lead.user.specialites || []).join(', ') || 'N/A'}</LeadsTd>
                    <LeadsTd>{JSON.stringify(lead.user.grades) || 'N/A'}</LeadsTd>
                    <LeadsTd>
                      {lead.user.quiz_info 
                        ? JSON.stringify(lead.user.quiz_info, null, 2)
                        : 'N/A'
                      }
                    </LeadsTd>
                  </Tr>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <p style={{ textAlign: 'center', marginTop: '2rem', color: '#666' }}>
              Aucun lead trouvé pour le moment. Créez un filtre ou ajustez la date pour commencer.
            </p>
          )}
        </TableContainer>
      </ContentContainer>
    </Menu>
  );
};

export default Leads; 